import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Space, Switch, QRCode, Alert, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, SafetyCertificateOutlined, QrcodeOutlined, DeleteOutlined, CrownOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { userAPI } from '../services/api';
import { showSuccessAlert, showErrorAlert, showAuthAlert } from '../utils/customAlert';
import { hasPermission, isSuperAdmin, isAdmin } from '../utils/auth';

const { Option } = Select;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // 添加刷新键
  // eslint-disable-next-line no-unused-vars
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  // 移除本地状态，使用权限检查函数
  const [googleAuthModal, setGoogleAuthModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [googleSecret, setGoogleSecret] = useState('');
  const [userPasswordModal, setUserPasswordModal] = useState(false);
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (forceRefresh = false) => {
    try {
      setLoading(true);
      // 添加强制刷新参数，避免缓存
      const response = await userAPI.getUsers(forceRefresh ? Date.now() : undefined);
      setUsers(response.data);
      if (forceRefresh) {
        setRefreshKey(prev => prev + 1); // 更新刷新键
      }
    } catch (error) {
      showErrorAlert('获取用户列表失败', '错误');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      isAdmin: user.isAdmin,
      isApproved: user.isApproved,
      password: '' // 编辑时密码字段为空，用户可以选择是否修改
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      // 自动trim所有字符串字段
      const trimmedValues = {
        ...values,
        username: values.username?.trim(),
        password: values.password?.trim()
      };
      
      // 如果是编辑用户且密码为空，则不更新密码
      if (editingUser && !trimmedValues.password) {
        // eslint-disable-next-line no-unused-vars
        const { password, ...updateData } = trimmedValues;
        await userAPI.updateUser(editingUser.id, updateData);
        showSuccessAlert('用户信息修改成功！', '修改成功');
        setModalVisible(false);
      } else if (editingUser && trimmedValues.password) {
        // 编辑用户且提供了新密码
        await userAPI.updateUser(editingUser.id, trimmedValues);
        showSuccessAlert('用户信息和密码修改成功！', '修改成功');
        setModalVisible(false);
      } else {
        // 添加用户
        await userAPI.createUser(trimmedValues);
        showSuccessAlert('用户添加成功！', '添加成功');
        // 添加成功后清空表单并关闭模态框
        form.resetFields();
        setModalVisible(false);
      }
      fetchUsers();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const data = error.response.data;
        showErrorAlert(data?.message || '操作失败', '错误');
      } else {
        showErrorAlert('操作失败，请重试', '错误');
      }
    }
  };

  const handleApprove = async (userId, approved) => {
    try {
      await userAPI.approveUser(userId);
      showSuccessAlert(approved ? '用户审核通过！' : '用户审核已撤销！', '审核成功');
      fetchUsers();
    } catch (error) {
      showErrorAlert('审核操作失败', '错误');
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingUser(null);
    form.resetFields();
  };

  // 谷歌验证码相关方法
  const handleGoogleAuth = async (user) => {
    try {
      setCurrentUser(user);
      const response = await userAPI.generateGoogleSecret(user.id);
      setQrCodeData(response.data.qrCode);
      setGoogleSecret(response.data.secret);
      setGoogleAuthModal(true);
    } catch (error) {
      showErrorAlert('获取谷歌验证码密钥失败', '错误');
    }
  };

  const handleEnableGoogleAuth = async (values) => {
    try {
      await userAPI.enableGoogleAuth(currentUser.id, {
        secret: googleSecret,
        googleToken: values.googleToken
      });
      showSuccessAlert('谷歌验证码启用成功！', '启用成功');
      setGoogleAuthModal(false);
      setGoogleSecret('');
      
      // 立即刷新用户列表，确保状态更新
      setTimeout(() => {
        fetchUsers(true); // 强制刷新
      }, 100);
    } catch (error) {
      showErrorAlert('启用谷歌验证码失败', '错误');
    }
  };

  const handleDisableGoogleAuth = async (user) => {
    try {
      await userAPI.disableGoogleAuth(user.id);
      showSuccessAlert('谷歌验证码禁用成功！', '禁用成功');
      
      // 立即刷新用户列表，确保状态更新
      setTimeout(() => {
        fetchUsers(true); // 强制刷新
      }, 100);
    } catch (error) {
      showErrorAlert('禁用谷歌验证码失败', '错误');
    }
  };

  const handleGoogleAuthCancel = () => {
    setGoogleAuthModal(false);
    setCurrentUser(null);
    setQrCodeData(null);
    setGoogleSecret('');
  };

  // 用户口令管理
  const handleUserPassword = (user) => {
    setCurrentUser(user);
    setUserPasswordModal(true);
    passwordForm.resetFields();
  };

  const handleUserPasswordCancel = () => {
    setUserPasswordModal(false);
    setCurrentUser(null);
    passwordForm.resetFields();
  };

  const handleSetUserPassword = async (values) => {
    try {
      await userAPI.setUserPassword(currentUser.id, values.userPassword);
      showSuccessAlert('用户口令设置成功', '成功');
      handleUserPasswordCancel();
      
      // 立即刷新用户列表，确保状态更新
      setTimeout(() => {
        fetchUsers(true); // 强制刷新
      }, 100);
    } catch (error) {
      showErrorAlert('设置用户口令失败', '错误');
    }
  };

  const handleResetUserPassword = async () => {
    try {
      await userAPI.resetUserPassword(currentUser.id);
      showSuccessAlert('用户口令已重置为默认口令: 123456', '成功');
      handleUserPasswordCancel();
      
      // 立即刷新用户列表，确保状态更新
      setTimeout(() => {
        fetchUsers(true); // 强制刷新
      }, 100);
    } catch (error) {
      showErrorAlert('重置用户口令失败', '错误');
    }
  };

  // 删除用户
  const handleDeleteUser = async (userId) => {
    try {
      await userAPI.deleteUser(userId);
      showSuccessAlert('用户删除成功', '成功');
      fetchUsers(true); // 强制刷新
    } catch (error) {
      showAuthAlert('删除用户失败', '错误');
    }
  };

  // 设置超级管理员
  const handleSetSuperAdmin = async (user) => {
    try {
      const newStatus = !user.isSuperAdmin;
      await userAPI.setSuperAdmin(user.id, newStatus);
      showSuccessAlert(
        newStatus ? '已设置为超级管理员' : '已取消超级管理员权限', 
        '成功'
      );
      fetchUsers(true); // 强制刷新
    } catch (error) {
      showAuthAlert('设置超级管理员失败', '错误');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: '角色',
      key: 'role',
      width: 120,
      render: (_, record) => {
        if (record.isSuperAdmin) {
          return <Tag color="gold" icon={<CrownOutlined />}>超级管理员</Tag>;
        } else if (record.isAdmin) {
          return <Tag color="red">管理员</Tag>;
        } else {
          return <Tag color="blue">普通用户</Tag>;
        }
      },
    },
    {
      title: '审核状态',
      dataIndex: 'isApproved',
      key: 'isApproved',
      width: 120,
      render: (isApproved) => (
        <Tag color={isApproved ? 'green' : 'orange'}>
          {isApproved ? '已审核' : '待审核'}
        </Tag>
      ),
    },
    {
      title: '谷歌验证码',
      dataIndex: 'isGoogleAuthEnabled',
      key: 'isGoogleAuthEnabled',
      width: 120,
      render: (isGoogleAuthEnabled, record) => (
        <Tag color={record.isAdmin && isGoogleAuthEnabled ? 'blue' : 'default'}>
          {record.isAdmin ? (isGoogleAuthEnabled ? '已启用' : '未启用') : '不适用'}
        </Tag>
      ),
    },
    {
      title: '用户口令',
      dataIndex: 'userPassword',
      key: 'userPassword',
      width: 120,
      render: (userPassword, record) => (
        <Tag color={!record.isAdmin && userPassword ? 'green' : 'default'}>
          {!record.isAdmin ? (userPassword ? '已设置' : '未设置') : '不适用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (_, record) => (
        <Space>
          {hasPermission('user_management') ? (
            <>
              <Button 
                type="link" 
                icon={<EditOutlined />} 
                onClick={() => handleEdit(record)}
                disabled={isAdmin() && record.isSuperAdmin}
              >
                编辑
              </Button>
              <Switch
                checked={record.isApproved}
                onChange={(checked) => handleApprove(record.id, checked)}
                checkedChildren="通过"
                unCheckedChildren="待审"
                disabled={isAdmin() && record.isSuperAdmin}
              />
              {record.isAdmin && !(isAdmin() && record.isSuperAdmin) && (
                record.isGoogleAuthEnabled ? (
                  <Button 
                    type="link" 
                    icon={<SafetyCertificateOutlined />}
                    onClick={() => handleDisableGoogleAuth(record)}
                    danger
                  >
                    禁用验证码
                  </Button>
                ) : (
                  <Button 
                    type="link" 
                    icon={<QrcodeOutlined />}
                    onClick={() => handleGoogleAuth(record)}
                  >
                    设置验证码
                  </Button>
                )
              )}
              {!record.isAdmin && !(isAdmin() && record.isSuperAdmin) && (
                <Button 
                  type="link" 
                  icon={<LockOutlined />}
                  onClick={() => handleUserPassword(record)}
                >
                  用户口令
                </Button>
              )}
              {hasPermission('user_delete') && !record.isSuperAdmin && (
                <Popconfirm
                  title="确定要删除这个用户吗？"
                  description="删除后无法恢复，请谨慎操作！"
                  onConfirm={() => handleDeleteUser(record.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button 
                    type="link" 
                    icon={<DeleteOutlined />}
                    danger
                  >
                    删除
                  </Button>
                </Popconfirm>
              )}
              {hasPermission('user_super_admin') && (
                <Button 
                  type="link" 
                  icon={<CrownOutlined />}
                  onClick={() => handleSetSuperAdmin(record)}
                  style={{ color: record.isSuperAdmin ? '#faad14' : '#1890ff' }}
                >
                  {record.isSuperAdmin ? '取消超级管理员' : '设为超级管理员'}
                </Button>
              )}
            </>
          ) : (
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              disabled
            >
              查看
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>用户管理</h2>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchUsers(true)} loading={loading}>
            刷新
          </Button>
          {hasPermission('user_management') && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              添加用户
            </Button>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
      />

      <Modal
        title={editingUser ? '编辑用户' : '添加用户'}
        open={modalVisible}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          preserve={false}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input disabled={!!editingUser} placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? '新密码（留空则不修改）' : '密码'}
            rules={[
              { 
                required: !editingUser, 
                message: editingUser ? '编辑时密码为可选' : '请输入密码' 
              },
              {
                min: 6,
                message: '密码长度至少6位'
              }
            ]}
          >
            <Input.Password placeholder={editingUser ? '留空则不修改密码' : '请输入密码'} />
          </Form.Item>

          <Form.Item
            name="isAdmin"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select>
              <Option value={false}>普通用户</Option>
              {isSuperAdmin() && <Option value={true}>管理员</Option>}
            </Select>
          </Form.Item>

          {editingUser && (
            <Form.Item
              name="isApproved"
              label="审核状态"
            >
              <Select>
                <Option value={false}>待审核</Option>
                <Option value={true}>已审核</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? '修改' : '添加'}
              </Button>
              <Button onClick={handleCancel}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 用户口令管理模态框 */}
      <Modal
        title="用户口令管理"
        open={userPasswordModal}
        onCancel={handleUserPasswordCancel}
        footer={null}
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleSetUserPassword}
        >
          <Form.Item label="用户名">
            <Input value={currentUser?.username} disabled />
          </Form.Item>
          
          <Form.Item
            name="userPassword"
            label="新用户口令"
            rules={[
              { required: true, message: '请输入用户口令!' },
              { min: 6, message: '用户口令至少6位!' }
            ]}
          >
            <Input.Password placeholder="请输入新的用户口令" />
          </Form.Item>

          <Alert
            message="用户口令说明"
            description="普通用户登录时需要输入此口令。管理员用户不需要用户口令。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                设置口令
              </Button>
              <Button onClick={handleResetUserPassword}>
                重置为默认口令
              </Button>
              <Button onClick={handleUserPasswordCancel}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 谷歌验证码设置模态框 */}
      <Modal
        title="设置谷歌验证码"
        open={googleAuthModal}
        onCancel={handleGoogleAuthCancel}
        footer={null}
        width={600}
      >
        {qrCodeData && (
          <div>
            <Alert
              message="设置谷歌验证码"
              description="请使用谷歌身份验证器扫描二维码，或手动输入密钥。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <QRCode value={qrCodeData} size={200} />
            </div>
            
            <Alert
              message="手动输入密钥"
              description={`如果无法扫描二维码，请手动输入以下密钥：${googleSecret}`}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form
              layout="vertical"
              onFinish={handleEnableGoogleAuth}
            >
              <Form.Item
                name="googleToken"
                label="验证码"
                rules={[
                  { required: true, message: '请输入6位验证码' },
                  { pattern: /^\d{6}$/, message: '验证码必须是6位数字' }
                ]}
              >
                <Input 
                  placeholder="请输入谷歌身份验证器显示的6位数字"
                  maxLength={6}
                />
              </Form.Item>
              
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    启用谷歌验证码
                  </Button>
                  <Button onClick={handleGoogleAuthCancel}>
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
} 